import { useNavigate, useParams } from 'react-router-dom';
import {
  useEditBrandingMutation,
  useGetBrandingQuery,
} from 'src/api/branding/hooks';
import { EditBrandingRequest } from 'src/api/branding/types';
import { useGlobalModals } from 'src/contexts/global-modals';
import {
  validateEmpty,
  validateHexColor,
  validateHexColorRequired,
} from 'src/helpers/validators';
import { links } from 'src/router/routes';

export type BrandingFormData = {
  name: string;
  color_1: string;
  color_2: string;
  color_3: string;
  color_4: string;
  color_5: string;
  main_logo: File | string | null;
  secondary_logo: File | string | null;
  tertiary_logo: File | string | null;
};

export const validate = {
  color_1: validateHexColorRequired,
  color_2: validateHexColor,
  color_3: validateHexColor,
  color_4: validateHexColor,
  color_5: validateHexColor,
  main_logo: (value: File | string | null) =>
    !value ? 'Please select file' : undefined,
  name: (value: string) => validateEmpty(value, 'Please enter name'),
};

export const useBrandingDetails = () => {
  const { id } = useParams<{ id: string }>();
  const brandingId = Number(id);

  const navigate = useNavigate();

  const { openLoadingModal, closeLoadingModal, openDeleteBrandingModal } =
    useGlobalModals();

  const { data: branding, isPending } = useGetBrandingQuery(brandingId);

  const { editBrandingMutation, isPending: isEditPending } =
    useEditBrandingMutation();

  const initialValues: Partial<BrandingFormData> = {
    color_1: branding?.color_1 || '',
    color_2: branding?.color_2 || '',
    color_3: branding?.color_3 || '',
    color_4: branding?.color_4 || '',
    color_5: branding?.color_5 || '',
    main_logo: branding?.main_logo || null,
    secondary_logo: branding?.secondary_logo || null,
    tertiary_logo: branding?.tertiary_logo || null,
    name: branding?.name || '',
  };

  const handleSubmit = async (data: BrandingFormData) => {
    if (!brandingId) return;

    const editData: EditBrandingRequest = {
      name: data.name,
      color_1: data.color_1,
      color_2: data.color_2 || undefined,
      color_3: data.color_3 || undefined,
      color_4: data.color_4 || undefined,
      color_5: data.color_5 || undefined,
      main_logo: data.main_logo,
      secondary_logo: data.secondary_logo,
      tertiary_logo: data.tertiary_logo,
    };

    openLoadingModal(
      'Editing branding',
      'Please wait while we edit the branding...'
    );

    try {
      await editBrandingMutation({
        id: brandingId,
        formData: editData,
      });
    } catch (error) {
      console.error(error);
    } finally {
      closeLoadingModal();
    }
  };

  const handleCancel = () => {
    navigate(links.brandings);
  };

  const handleDelete = () => {
    openDeleteBrandingModal(brandingId);
  };

  return {
    branding,
    brandingId,
    isEditPending,
    isPending,
    handleSubmit,
    handleCancel,
    handleDelete,
    initialValues,
    validate,
  };
};
